import React, { FC, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useHistory } from "react-router";
import { FormInput } from "go-form";
import useFlash from "go-alert/AlertMessage";
import handleError from "go-app/services/errors";
import { FormDirty } from "go-form/components/FormDirty";
import { useCustomErrors } from "go-form/hooks";
import { CustomValidationConfig, CustomValidationConstraint } from "go-form/services/types";
import { selectOrganization } from "go-security/services/organizations/selectors";
import { StockApi } from "../../../../../../../../../services/Api/Organization/types";
import { api } from "../../../../../../../../../services/Api/api";

interface Props {
	stock?: StockApi;
}

const StockForm: FC<Props> = (props) => {
	const { stock } = props;
	const [loading, setLoading] = useState<boolean>(false);
	const organization = useSelector(selectOrganization);
	const history = useHistory();
	const { t } = useTranslation();
	const form = useForm<StockApi>({
		criteriaMode: "all",
		defaultValues: { ...stock },
	});
	const {
		register,
		formState,
		formState: { errors },
		reset,
		handleSubmit,
		watch,
		setError,
	} = form;
	const { addFlash, addSuccessFlash } = useFlash();
	const { setErrors, validateCustomErrors } = useCustomErrors(setError);

	const onSubmit = handleSubmit(async (data: StockApi) => {
		if (!validateCustomErrors()) {
			return;
		}

		try {
			setLoading(true);
			if (stock?.id) {
				const res = await api.organization().updateStock(data);
				reset(res);
			} else {
				await api.organization().createStock(data);
				history.push(`/${organization.id}/settings/stocks`);
			}
			addSuccessFlash(t("common.flash.saved", { ns: "lib" }));
		} catch (err) {
			handleError.form(err, setError, addFlash);
		}
		setLoading(false);
	});

	const nameValidationConfig = useMemo(
		() => ({
			types: [{ constraint: CustomValidationConstraint.REQUIRED }],
			setErrors,
			utils: { watch },
		}),
		[setErrors, watch]
	) satisfies CustomValidationConfig;

	return (
		<FormDirty key="settings-stocks-form" formState={formState} loading={loading} onSubmit={onSubmit}>
			<FormInput
				name="name"
				errors={errors}
				register={register}
				label={t("common.word.name", { ns: "lib" })}
				customValidationConfig={nameValidationConfig}
			/>
		</FormDirty>
	);
};

export default StockForm;
